import React, { ChangeEvent, Component, type JSX } from 'react';
import { events } from '../../util/Events';
import { FilterOptions, SortType } from './SearchBooster';

const distances = [
  { value: '500', label: 'Alle afstanden' },
  { value: '11', label: 'Minder dan 10 km' },
  { value: '27', label: 'Minder dan 25 km' },
  { value: '55', label: 'Minder dan 50 km' },
  { value: '105', label: 'Minder dan 100 km' },
  { value: '155', label: 'Minder dan 150 km' },
];

const monthLabel: string[] = ['januari', 'februari', 'maart', 'april', 'mei', 'juni', 'juli', 'augustus', 'september', 'oktober', 'november', 'december'];

interface FilterControlsProps {
  city?: string;
  filterOptions: FilterOptions;
  types: string[];
}

/**
 * Render the select boxes to filter the search results
 *      Distance (only if city has been provided)
 *      Months
 *      Training Type
 *      Sort on distance or date
 */
export class FilterControls extends Component<FilterControlsProps> {

  public render(): JSX.Element {
    let { filterOptions, types, city } = this.props;
    return (
      <div className="search-filter">
        {city && city.length > 0
        && <DistanceDropdown/>
        }

        <MonthDropdown filterOptions={filterOptions}/>

        {types && types.length > 1
        && <TypeDropdown filterOptions={filterOptions} types={types}/>
        }

        <SortToggle filterOptions={filterOptions}/>
      </div>);
  }
}

/**
 * Renders the distance list
 */
export const DistanceDropdown = (): JSX.Element => {
  return (
    <div className="search-filter__group search-filter__group--distance">
      <div className="search-filter__label">
        km vanaf jouw locatie:
      </div>
      <select className="search-filter__select"
        name="distance"
        onChange={(e: ChangeEvent<HTMLSelectElement>): void => events.emit('SearchBoosterFilterChanged', e)}>
        {distances.map((distance, key) => {
          return (
            <option key={key} value={distance.value}>
              {distance.label}
            </option>);
        })}
      </select>
    </div>
  );
};

interface MonthDropdownProps {
  filterOptions: FilterOptions;
}

/**
 * Renders the month list for the next few months
 */
export class MonthDropdown extends Component<MonthDropdownProps> {

  /**
   * Returns array with the next up coming months
   */
  public getUpComingMonths(numberMonth: number): { value: string; label: string }[] {
    let time = new Date();
    let month = time.getMonth() + 1;
    let year = time.getFullYear();
    let months = [];

    for (let i = 1; i < numberMonth; i++) {
      let label = monthLabel[month - 1].charAt(0).toUpperCase()
        + monthLabel[month - 1].slice(1);
      months.push({
        value: `${year}-${('0' + month).slice(-2)}`,
        label: `${label}`,
      });
      if (month >= 12) {
        month = 1;
        year++;
      } else {
        month++;
      }
    }

    return months;
  }

  public render(): JSX.Element {
    let { filterOptions } = this.props;
    const { start_date, end_date } = filterOptions.acfData;
    const acfPeriod = start_date !== '' && end_date !== '';
    let months = this.getUpComingMonths(8); // get the next 8 months
    let monthsOptions = months.map((month, key) => {
      return (
        <option key={key} value={month.value}>{month.label}</option>
      );
    });

    let select = (
      <select className="search-filter__select"
        name="month"
        value={filterOptions.filter.month}
        disabled={acfPeriod}
        onChange={(e: ChangeEvent<HTMLSelectElement>): void => events.emit('SearchBoosterFilterChanged', e)}>
        <option value="">Alle maanden</option>
        {monthsOptions}
      </select>
    );

    return (
      <div className="search-filter__group search-filter__group--period">
        <div className="search-filter__label">maand:</div>
        {select}
      </div>
    );
  }
}

interface TypeDropdownInterface {
  filterOptions: FilterOptions;
  types: string[];
}

/**
 * Renders the training type dropdown
 */
export const TypeDropdown = (props: TypeDropdownInterface): JSX.Element => {
  let { filterOptions, types } = props;

  const trainingTypes = types.map((type, key) => {
    return (
      <option key={key} value={type}>{type}</option>
    );
  });

  return (
    <div className="search-filter__group search-filter__group--period">
      <div className="search-filter__label">type training:</div>
      <select className="search-filter__select search-filter__select--type"
        name="type"
        value={filterOptions.filter.type}
        onChange={(e: ChangeEvent<HTMLSelectElement>): void => events.emit('SearchFilterChanged', e)}>
        <option value="">Alle trainingen</option>
        {trainingTypes}
      </select>
    </div>
  );
};

interface SortToggleProps {
  filterOptions: FilterOptions;
}

/**
 * Renders the sort toggle -> sort on distance or data
 */
export const SortToggle = (props: SortToggleProps): JSX.Element => {
  let { filterOptions } = props;

  const order = filterOptions.sort;
  const classDistance = `search-filter__toggle ${order === SortType.DISTANCE ? 'search-filter__toggle--active' : ''} search-filter__toggle--left`;
  const classDate = `search-filter__toggle ${order === SortType.DISTANCE ? '' : 'search-filter__toggle--active'} search-filter__toggle--right`;

  const toggle = (
    <div className="search-filter__toggle-container">
      <button
        onClick={(e: React.MouseEvent): void => events.emit('SearchFilterChanged', e)}
        name="sort"
        type="button"
        className={classDistance}
        value="distance">
        Afstand
      </button>
      <button
        onClick={(e: React.MouseEvent): void => events.emit('SearchFilterChanged', e)}
        name="sort"
        type="button"
        className={classDate}
        value="date">
        Datum
      </button>
    </div>
  );

  return (
    <div className="search-filter__group search-filter__group--sort">
      <div className="search-filter__label search-filter__sort-label">sorteer op:</div>
      {toggle}
    </div>
  );
};
